import { useCallback, useMemo } from "react";

import { skipToken } from "@reduxjs/toolkit/dist/query";
import {
    AssetClass,
    BsMetadata,
    OracleQuote,
    OrcaPositionAmounts,
    TokenPositionExpanded,
    WhirlpoolPositionExpanded
} from "@bridgesplit/abf-sdk";
import { filterTruthy, formatAddress, lamportsToUiAmount, removeDuplicates } from "@bridgesplit/utils";

import { useBsMetadataByMints, useWhirlpoolPositionsQuery } from "../reducers";
import { useEscrowedAssets, useUserUnrecognizedAssets } from "./assets";
import { getUsdAmountFromUsdPrice, useOraclePrices } from "./pricing";
import { useActiveEscrow, useEscrowPreference } from "./escrow";
import { TokenBalanceExpanded } from "../types";

export const MAX_TICK_INDEX = 443636;
export const MIN_TICK_INDEX = -443636;
export const MAX_SQRT_PRICE = "79226673515401279992447579055";
export const MIN_SQRT_PRICE = "4295048016";

export function useUserWhirlpoolPositions(options?: { skip?: boolean }) {
    const { escrowNeeded } = useEscrowPreference();
    const { activeEscrow } = useActiveEscrow();
    const { data: escrows } = useEscrowedAssets(
        { escrow_accounts: activeEscrow ? [activeEscrow] : [] },
        { skip: !activeEscrow || !!options?.skip }
    );
    const { data: walletAssets } = useUserUnrecognizedAssets(!!escrowNeeded || !!options?.skip);

    const nftMints = useMemo(() => {
        if (escrowNeeded) {
            return escrows?.filter((e) => e.amount === 1).map((e) => e.mint);
        }
        return walletAssets?.filter((d) => d.tokenAmount.decimals === 0).map((d) => d.mint);
    }, [escrowNeeded, escrows, walletAssets]);
    return useWhirlpoolPositions(nftMints, options);
}

export function useWhirlpoolPositions(nftMints: string[] | undefined, options?: { skip?: boolean }) {
    const {
        data: rawData,
        isLoading: whirlpoolLoading,
        isFetching
    } = useWhirlpoolPositionsQuery(nftMints ? removeDuplicates(nftMints) : skipToken, {
        skip: !nftMints?.length || options?.skip
    });

    const mints = Object.values(rawData?.whirlpools ?? {})
        .map((w) => [w.tokenMintA, w.tokenMintB])
        .flat();

    const { getOracle, isLoading: pricesLoading } = useOraclePrices(mints);
    const { getMetadata, isLoading: metadataLoading } = useBsMetadataByMints(mints);

    const isLoading = whirlpoolLoading || pricesLoading || metadataLoading;

    const getTokenDetail = useCallback(
        (mint: string, lamportAmount: number): (TokenPositionExpanded & { oracle: OracleQuote }) | undefined => {
            const metadata = getMetadata(mint);
            const oracle = getOracle(mint);
            if (!metadata || !oracle) return undefined;
            const amount = lamportsToUiAmount(lamportAmount, metadata.decimals);
            return {
                mint,
                decimals: metadata.decimals,
                metadata,
                usdPrice: oracle.usdPrice,
                oracle,
                usdAmount: oracle.getUsdAmount(amount),
                amount
            };
        },
        [getMetadata, getOracle]
    );

    const data = useMemo(() => {
        if (nftMints?.length === 0) return [];
        if (!rawData || isLoading) return undefined;
        const allPositions = Object.values(rawData.whirlpoolsToPositions).flat();

        return allPositions
            .map((position): WhirlpoolPositionExpanded | null => {
                const whirlpool = rawData.whirlpools[position.whirlpool];
                if (!whirlpool) return null;
                const tokenA = getTokenDetail(whirlpool.tokenMintA, position.liquidityAmounts[whirlpool.tokenMintA]);
                const tokenB = getTokenDetail(whirlpool.tokenMintB, position.liquidityAmounts[whirlpool.tokenMintB]);
                const whirlpoolMetadata = getMetadata(position.whirlpool);

                if (!tokenA || !tokenB || !whirlpoolMetadata) return null;
                const totalPrice = tokenA.usdAmount + tokenB?.usdAmount;

                const oracle: OracleQuote = {
                    usdPrice: totalPrice,
                    getUsdAmount: (...params) => getUsdAmountFromUsdPrice(totalPrice, ...params),
                    baseQuotes: [...tokenA.oracle.baseQuotes, ...tokenB.oracle.baseQuotes]
                };

                return {
                    totalPrice,
                    whirlpoolAddress: position.whirlpool,
                    whirlpoolMetadata,
                    tokenA,
                    tokenB,
                    whirlpool,
                    position,
                    oracle
                };
            })
            .filter(filterTruthy);
    }, [getMetadata, getTokenDetail, isLoading, nftMints?.length, rawData]);

    return { data, isLoading, isFetching };
}

const ORCA_IDENTIFIER = "orcaHYtdWqb5GKfa4dGuCEYTYB1pcfCsAWmk3M9u4WQ";
export function isWhirlpoolMetadata(metadata: BsMetadata | undefined) {
    if (!metadata) return false;
    return metadata.assetClass === AssetClass.LpPosition && metadata.assetOriginator === ORCA_IDENTIFIER;
}

export const whirlpoolPositionToToken = (whirlpoolPosition: WhirlpoolPositionExpanded): TokenBalanceExpanded => {
    const {
        whirlpoolMetadata,
        position: { positionMint: mint }
    } = whirlpoolPosition;
    return {
        amount: 1,
        metadata: {
            ...whirlpoolMetadata,
            assetMint: mint,
            name: `Orca LP (${formatAddress(mint)})`
        },
        key: mint,
        mint,
        whirlpoolPosition
    };
};

export function parseOrcaAmounts(whirlpoolPosition: WhirlpoolPositionExpanded, amounts: OrcaPositionAmounts) {
    return Object.entries(amounts).map(([mint, amount]) => {
        const token = mint === whirlpoolPosition.tokenA.mint ? whirlpoolPosition.tokenA : whirlpoolPosition.tokenB;
        const uiAmount = lamportsToUiAmount(amount, token.metadata.decimals);
        const usdAmount = uiAmount * token.usdPrice;

        return { mint, uiAmount, usdAmount };
    });
}

export function getClaimableWhirlpoolFees(whirlpoolPosition?: WhirlpoolPositionExpanded) {
    if (!whirlpoolPosition) return 0;
    const { position } = whirlpoolPosition;
    const orcaAmounts = parseOrcaAmounts(whirlpoolPosition, position.feeAmounts);
    return orcaAmounts.reduce((prev, curr) => prev + curr.usdAmount, 0);
}
